import type { DesignNavigationDelivery } from './navigation-delivery';
import type { NavigationDeliveryEntry } from './navigation-delivery-store';
import { normalizeDesignTargetThreadRouteRefs } from './shared';

/** The server's receipt lookup accepts at most this many inputs per call. */
export const MAX_NAVIGATION_RECONCILE_INPUTS = 50;

export type NavigationDeliveryTarget = {
  routeRef: string;
  clientInputId: string;
  create: boolean;
};

/** Every destination a job still has to deliver, keyed exactly as the send path keys it. */
export function navigationDeliveryTargets(
  job: DesignNavigationDelivery
): NavigationDeliveryTarget[] {
  const target = (routeRef: string, create: boolean) => ({
    routeRef,
    clientInputId: `${job.id}:${routeRef}`,
    create,
  });
  return [
    ...normalizeDesignTargetThreadRouteRefs(job.options).map((routeRef) =>
      target(routeRef, false)
    ),
    ...(job.options.newThreadRouteRefs ?? []).map((routeRef) =>
      target(routeRef, true)
    ),
  ];
}

/**
 * Entries whose outcome this app instance cannot observe: restored from
 * storage, or already marked failed. A live `pending` run settles itself.
 */
export function needsNavigationReconcile(
  entry: NavigationDeliveryEntry,
  actorId: string
): boolean {
  return entry.job.actorId === actorId && entry.status !== 'pending';
}

/** Stable, bounded lookup inputs for the current actor's unsettled entries. */
export function navigationReconcileInputs(
  entries: readonly NavigationDeliveryEntry[],
  actorId: string | null | undefined
): Array<{ routeRef: string; clientInputId: string }> {
  if (!actorId) return [];
  const collect = (status: NavigationDeliveryEntry['status']) => {
    const inputs = new Map<
      string,
      { routeRef: string; clientInputId: string }
    >();
    for (const entry of entries) {
      if (entry.status !== status || !needsNavigationReconcile(entry, actorId))
        continue;
      for (const { routeRef, clientInputId } of navigationDeliveryTargets(
        entry.job
      ))
        inputs.set(clientInputId, { routeRef, clientInputId });
    }
    return Array.from(inputs.values()).sort((a, b) =>
      a.clientInputId.localeCompare(b.clientInputId)
    );
  };
  // Unconfirmed entries stay hidden until checked, so they claim the budget first.
  const unconfirmed = collect('unconfirmed');
  const seen = new Set(unconfirmed.map((input) => input.clientInputId));
  return [
    ...unconfirmed,
    ...collect('failed').filter((input) => !seen.has(input.clientInputId)),
  ].slice(0, MAX_NAVIGATION_RECONCILE_INPUTS);
}

export type NavigationReconcileOutcome =
  | { kind: 'unchanged' }
  | { kind: 'accepted' }
  | { kind: 'update'; entry: NavigationDeliveryEntry };

/**
 * Apply a server receipt lookup to one entry. Accepted targets are dropped;
 * an entry with none left was delivered. An unconfirmed entry becomes failed
 * only when every remaining target was part of the lookup.
 */
export function reconcileNavigationEntry(
  entry: NavigationDeliveryEntry,
  checked: ReadonlySet<string>,
  accepted: ReadonlySet<string>
): NavigationReconcileOutcome {
  const targets = navigationDeliveryTargets(entry.job);
  const remaining = targets.filter(
    (target) => !accepted.has(target.clientInputId)
  );
  if (targets.length > 0 && remaining.length === 0) return { kind: 'accepted' };
  const settled =
    entry.status === 'unconfirmed' &&
    remaining.every((target) => checked.has(target.clientInputId));
  if (remaining.length === targets.length && !settled)
    return { kind: 'unchanged' };
  const job =
    remaining.length === targets.length
      ? entry.job
      : {
          ...entry.job,
          options: {
            ...entry.job.options,
            targetThreadRouteRef: undefined,
            targetThreadRouteRefs: remaining
              .filter((target) => !target.create)
              .map((target) => target.routeRef),
            newThreadRouteRefs: remaining
              .filter((target) => target.create)
              .map((target) => target.routeRef),
            newThreadCount: 0,
          },
        };
  return {
    kind: 'update',
    entry: {
      job,
      status: settled ? 'failed' : entry.status,
      ...(entry.reason ? { reason: entry.reason } : {}),
    },
  };
}
